import { ACTIONS, type Action, RESERVED_CODES, rebind } from "../game/input/bindings";
import type { TouchMode } from "../game/input/touch";
import type { SpeedZoom } from "../game/render/camera";
import type { ResolutionMode } from "../game/resolution";
import { defaultSettings, type MotionPreference, type Settings } from "../game/settings";
import { cleanPilotName, PILOT_NAME_MAX } from "../net/protocol";
import { el } from "./dom";
import { ACTION_LABELS } from "./help-page";
import { keyLabel } from "./key-labels";
import type { ScreenManager } from "./screen-manager";

export interface SettingsPageContext {
	settings: Settings;
	/** Persist and apply a changed settings object. */
	onChange(settings: Settings): void;
	ui: ScreenManager;
	toggleFullscreen(): void;
	/** Short sound so the volume can be judged after moving the slider. */
	playTestSound(): void;
}

const PLAYER_NAMES = ["Blue (player 1)", "Red (player 2)", "Green (player 3)", "Yellow (player 4)"];
const PLAYER_CLASSES = ["p-blue", "p-red", "p-green", "p-yellow"];
const COLOR_PLACEHOLDERS = ["Blue", "Red", "Green", "Yellow"];

/** Sound, key bindings and display options. Every change is applied and saved at once. */
export function settingsContent(ctx: SettingsPageContext): HTMLElement {
	let settings = ctx.settings;
	const update = (next: Settings) => {
		settings = next;
		ctx.onChange(next);
	};

	// --- Sound ---
	const volumeValue = el("output", { class: "setting-value" });
	const volume = el("input", {
		type: "range",
		min: 0,
		max: 100,
		step: 5,
		value: Math.round(settings.volume * 100),
		"aria-label": "Volume",
	});
	const showVolume = () => {
		volumeValue.textContent = `${volume.value}%`;
	};
	volume.addEventListener("input", () => {
		showVolume();
		update({ ...settings, volume: Number(volume.value) / 100 });
	});
	volume.addEventListener("change", () => ctx.playTestSound());
	showVolume();

	const sound = toggle("Sound", !settings.muted, (on) => update({ ...settings, muted: !on }));

	// --- Pilots (P11.8) ---
	const nameFields = PLAYER_NAMES.map((label, i) => {
		const input = el("input", {
			type: "text",
			class: "pilot-name",
			maxlength: PILOT_NAME_MAX,
			value: settings.pilotNames[i] ?? "",
			placeholder: COLOR_PLACEHOLDERS[i] ?? "",
			autocomplete: "off",
			spellcheck: "false",
			"aria-label": `Name of ${label}`,
		});
		input.addEventListener("input", () => {
			const pilotNames = [...settings.pilotNames];
			pilotNames[i] = cleanPilotName(input.value);
			update({ ...settings, pilotNames });
		});
		return el("label", { class: `setting-row ${PLAYER_CLASSES[i] ?? ""}` }, label, input);
	});
	const namesHint = el(
		"p",
		{ class: "menu-hint" },
		"Shown in the HUD, on the results and in replays. Player 1's name is also used online.",
	);

	// --- Controls ---
	const status = el("p", { class: "menu-hint", "aria-live": "polite" });
	const bindingButtons = new Map<string, HTMLButtonElement>();
	const refreshBindings = () => {
		settings.bindings.forEach((b, p) => {
			for (const action of ACTIONS) {
				const button = bindingButtons.get(`${p}:${action}`);
				if (button) button.textContent = b[action].map(keyLabel).join(" / ") || "—";
			}
		});
	};

	const startRebind = (player: number, action: Action, button: HTMLButtonElement) => {
		const label = `${PLAYER_NAMES[player]}: ${ACTION_LABELS[action]}`;
		button.textContent = "Press a key…";
		button.classList.add("capturing");
		status.textContent = `${label}: press the new key (Esc cancels).`;
		ctx.ui.captureNextKey((ev) => {
			button.classList.remove("capturing");
			if (ev.code === "Escape") {
				status.textContent = "Cancelled.";
			} else if (RESERVED_CODES.has(ev.code)) {
				status.textContent = `${keyLabel(ev.code)} is reserved (pause, fullscreen, mute).`;
			} else {
				update({ ...settings, bindings: rebind(settings.bindings, player, action, ev.code) });
				status.textContent = `${label} → ${keyLabel(ev.code)}`;
			}
			refreshBindings();
		});
	};

	const headerCells = PLAYER_NAMES.map((name, i) =>
		el("th", { class: PLAYER_CLASSES[i] ?? "" }, name),
	);
	const rows = ACTIONS.map((action) => {
		const cells = PLAYER_NAMES.map((name, player) => {
			const button = el("button", {
				class: "btn btn-key",
				type: "button",
				"aria-label": `${name} ${ACTION_LABELS[action]}`,
			});
			button.addEventListener("click", () => startRebind(player, action, button));
			bindingButtons.set(`${player}:${action}`, button);
			return el("td", {}, button);
		});
		return el("tr", {}, el("th", { scope: "row" }, ACTION_LABELS[action]), ...cells);
	});
	const bindingsTable = el(
		"table",
		{ class: "stats-table bindings-table" },
		el("thead", {}, el("tr", {}, el("th", {}, "Action"), ...headerCells)),
		el("tbody", {}, ...rows),
	);
	refreshBindings();

	const resetKeys = el("button", { class: "btn btn-small", type: "button" }, "Reset keys");
	resetKeys.addEventListener("click", () => {
		update({ ...settings, bindings: defaultSettings().bindings });
		refreshBindings();
		status.textContent = "Keys reset to the original layout.";
	});

	const touchOrder: TouchMode[] = ["auto", "on", "off"];
	const touchState = el("span", { class: "toggle-state" }, TOUCH_LABELS[settings.touchControls]);
	const touch = el(
		"button",
		{ class: "btn btn-toggle", type: "button", "aria-label": "Touch controls" },
		el("span", {}, "On-screen touch controls"),
		touchState,
	);
	touch.addEventListener("click", () => {
		const next =
			touchOrder[(touchOrder.indexOf(settings.touchControls) + 1) % touchOrder.length] ?? "auto";
		touchState.textContent = TOUCH_LABELS[next];
		update({ ...settings, touchControls: next });
	});

	// --- Display ---
	const fullscreen = el("button", { class: "btn btn-small", type: "button" }, "Toggle fullscreen");
	fullscreen.addEventListener("click", () => ctx.toggleFullscreen());
	const resolutionOrder: ResolutionMode[] = ["auto", "sharp", "standard"];
	const resolutionState = el(
		"span",
		{ class: "toggle-state" },
		RESOLUTION_LABELS[settings.resolution],
	);
	const hiDpi = el(
		"button",
		{ class: "btn btn-toggle", type: "button", "aria-label": "Sharp rendering" },
		el("span", {}, "Sharp rendering on high-DPI screens"),
		resolutionState,
	);
	hiDpi.addEventListener("click", () => {
		const next =
			resolutionOrder[
				(resolutionOrder.indexOf(settings.resolution) + 1) % resolutionOrder.length
			] ?? "auto";
		resolutionState.textContent = RESOLUTION_LABELS[next];
		update({ ...settings, resolution: next });
	});

	const lookAhead = toggle(
		"Look-ahead camera (see further where you fly)",
		settings.lookAhead,
		(on) => update({ ...settings, lookAhead: on }),
	);
	const zoomState = el("span", { class: "toggle-state" }, ZOOM_LABELS[settings.speedZoom]);
	const speedZoom = el(
		"button",
		{ class: "btn btn-toggle", type: "button", "aria-label": "Zoom out at speed" },
		el("span", {}, "Zoom out at speed"),
		zoomState,
	);
	speedZoom.addEventListener("click", () => {
		const next: SpeedZoom = settings.speedZoom === "classic" ? "wide" : "classic";
		zoomState.textContent = ZOOM_LABELS[next];
		update({ ...settings, speedZoom: next });
	});

	const motionLabels: Record<MotionPreference, string> = {
		system: "System",
		on: "On",
		off: "Off",
	};
	const motionOrder: MotionPreference[] = ["system", "on", "off"];
	const motionState = el("span", { class: "toggle-state" }, motionLabels[settings.reducedMotion]);
	const motion = el(
		"button",
		{ class: "btn btn-toggle", type: "button", "aria-label": "Reduced motion" },
		el("span", {}, "Reduced motion (screen shake, animations)"),
		motionState,
	);
	motion.addEventListener("click", () => {
		const next =
			motionOrder[(motionOrder.indexOf(settings.reducedMotion) + 1) % motionOrder.length] ??
			"system";
		motionState.textContent = motionLabels[next];
		update({ ...settings, reducedMotion: next });
	});
	const markers = toggle("Pilot markers (P1 / P2 / AI tags)", settings.pilotMarkers, (on) =>
		update({ ...settings, pilotMarkers: on }),
	);

	return el(
		"div",
		{ class: "settings" },
		section("Sound", el("label", { class: "setting-row" }, "Volume", volume, volumeValue), sound),
		section("Pilots", ...nameFields, namesHint),
		section("Controls", bindingsTable, status, resetKeys, touch),
		section("Display", hiDpi, lookAhead, speedZoom, fullscreen),
		section("Accessibility", markers, motion),
	);
}

/** Auto = sharp until frames get slow, then 1× for the session (P10.13). */
const RESOLUTION_LABELS: Record<ResolutionMode, string> = {
	auto: "Auto",
	sharp: "On",
	standard: "Off",
};
const TOUCH_LABELS: Record<TouchMode, string> = { auto: "Auto", on: "On", off: "Off" };
const ZOOM_LABELS: Record<SpeedZoom, string> = { classic: "Classic", wide: "Wide" };

function section(title: string, ...children: Node[]): HTMLElement {
	return el("section", { class: "settings-section" }, el("h2", {}, title), ...children);
}

/** An on/off switch as a button with aria-pressed (keyboard-friendly in menus). */
function toggle(label: string, initial: boolean, onChange: (on: boolean) => void): HTMLElement {
	let on = initial;
	const state = el("span", { class: "toggle-state" });
	const button = el(
		"button",
		{ class: "btn btn-toggle", type: "button" },
		el("span", {}, label),
		state,
	);
	const render = () => {
		button.setAttribute("aria-pressed", String(on));
		state.textContent = on ? "On" : "Off";
	};
	button.addEventListener("click", () => {
		on = !on;
		render();
		onChange(on);
	});
	render();
	return button;
}
